"use client"

import { Skeleton } from "@/components/ui/skeleton"
import { Calendar } from "lucide-react"
import { useContractHistory } from "./useGetHistory"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Link } from "react-router-dom"

function ContractHistory({ solicitation_id }) {
  const { data, isLoading, error } = useContractHistory(solicitation_id)

  if (isLoading) {
    return (
      <div className="w-full py-6">
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    )
  }

  if (error) {
    return <div className="text-red-600 py-4 text-center">Error loading contract history</div>
  }

  if (!data || data.length === 0) {
    return <div className="text-gray-500 py-4 text-center">No contract history available</div>
  }

  // Sort history by date (oldest first for left-to-right timeline)
  const sortedHistory = [...data].sort((a, b) => {
    const dateA = a.posted_date ? new Date(a.posted_date).getTime() : 0
    const dateB = b.posted_date ? new Date(b.posted_date).getTime() : 0
    return dateA - dateB // Ascending order (oldest to newest)
  })

  return (
    <div className="w-full py-8">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-0 right-0 h-1 bg-gray-200 top-1/2 transform -translate-y-1/2" />

        {/* Timeline items */}
        <div className="relative flex justify-between">
          {sortedHistory.map((item, index) => (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative flex flex-col items-center group">
                    {/* Timeline dot with pulse effect */}
                    <div className="z-10 w-5 h-5 rounded-full bg-primary border-2 border-white shadow-md transition-transform duration-200 group-hover:scale-125" />

                    {/* Content below the dot */}
                    <div className="mt-3 text-center">
                      {/* Make the badge clickable with React Router Link */}
                      <Link to={`/contract/federal/${item.noticeId}`} className="inline-block">
                        <Badge
                          variant={getVariantByType(item.type)}
                          className="whitespace-nowrap px-3 py-1 font-medium transition-all duration-200 hover:shadow-md"
                        >
                          {item.type || "Update"}
                        </Badge>
                      </Link>

                      {/* Date with icon */}
                      <div className="text-xs text-gray-500 mt-2 flex items-center justify-center">
                        <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                        {formatDate(item.posted_date)}
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="p-3 max-w-xs">
                  <p className="text-sm">
                    {item.description || `${item.type} on ${formatDate(item.posted_date, true)}`}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>
    </div>
  )
}

// Helper: format dates
function formatDate(dateString, full = false) {
  if (!dateString) return "N/A"

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return "Invalid date"

  if (full) {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Helper: assign badge variants
function getVariantByType(type) {
  if (!type) return "default"

  const typeMap = {
    Posted: "success",
    Updated: "warning",
    Amended: "secondary",
    Awarded: "destructive",
    Cancelled: "outline",
  }

  return typeMap[type] || "default"
}

export { ContractHistory }
